import { authorizationType } from '#authorization/constants'
import { Authorization } from '#authorization/model'
import { delistingType } from '#delisting/constants'
import { Delisting } from '#delisting/model'
import { bowTag, carrierKindTag, contactTypeCategory, detailsType, fishingKindTag, loaTag, operatorTag, ownerTag, portRegistryCategory, portsideTag, starboardTag, tonnageTag, totalVolumeTag, vesselKindCategory, vesselRangeCategory, vesselTypeCategory } from '#details/constants'
import { Contact, Details, isOperatingCompany } from '#details/model'
import { imoscheme, ircsscheme, regnoscheme } from '#vid/constants'
import { photographsTypeCategory, photographType } from '#photographs/constants'
import { Photographs } from '#photographs/model'
import { SubmissionRef } from '#submission/model'
import { RecordIssue } from '#submission/validator'
import { trxauthzType } from '#trxauth/constants'
import { TrxAuthzs } from '#trxauth/model'
import { useVID, Vid } from '#vid/model'
import { Lifecycle } from 'apprise-core/common'
import { useT } from 'apprise-frontend-core/intl/language'

import { avlType } from '#avl/constants'
import { AvlDeclarations } from '#avl/model'
import { useAppSettings } from '#settings/settings'
import { TenantResource } from 'apprise-core/tenant'
import { useTagCache } from 'apprise-frontend-tags/cache'
import { priorTrxauthzType } from '#trxauth.prior/constants'
import { PriorTrxAuthzs } from '#trxauth.prior/model'




/** 
 * common to all records and record patches. 
 */
type AbstractRecord = TenantResource & {

    /** 
     * unique and internal, opaque from a domain perspective. 
     */
    id: RecordIdentifier,

    /** 
     *  dates the information at its source, not necessarily its ingestion.
     *  basis for ordering into history, identifies the current record. 
     */
    timestamp: string

    /**
     *  the primary vessel identifier.
     */
    uvi: Vid

    /**
     *  lifecycle metadata of the record/patch as it broadens its audience through the system.
     */
    lifecycle: Lifecycle<RecordState>

    /**
     *  since 06/2025: the scope of the record, as in the Record it belongs to.
     *  `rav` is the implicit scope for older records that don't specify it.
     *  'external` is the explicit scope of newer records that are not in the RAV, a pseuedo Record. 
     *  and yet are reported about via the app in compliance with some CMM
     *  typically these are CMMs associated with various "Lists", such as the AVL.
     *  
     *  note: additional Records are not foresseen for now, but they would threotically add up. 
     *  `external` would always remain as the scope of all records that aren't in a real Record.
     */
    scope?: RecordScope

    /**
     *  the slots that changed to make this the current record.
     */
    patchedSlots: SlotType[]


    /**
     *  the original submission for this record, if any.
     */
    origin?: SubmissionRef

    /**
     *  since 06/2025: the CMM gverning the reporting flow that created the record.
     *  it's should capture the template of the original submission of the record, hence its "provenance".
     *  it's should also be consistent with the patchedSlots, as it is a higher-level interpretation of the slots relevant to the flow. 
     *  the implicit CMM of records that do not specify it is 19/02 for Fishing Vessels and 23/05 for Carrier Vessels.
     *  other CMMs are associated with various lists, such as the AVL.
     */
    cmm?: '19/04' | '23/05' | '10/08' | '25/05'


    /**
     *  record aliases, such as originate from merges.
     */
    aliases: RecordAlias[]

}

export type RecordScope = 'rav' | 'external'

export type RecordAlias = {

    uvi: Vid
    comment: string
    since: string
    aliasedBy: string
}


/**
 *  a "partial" record with some slots only, often just one.
 *  serves as a patch to update the current record.
 */
export type RecordPatch<T extends SlotType = never> = AbstractRecord & Pick<RecordSlots, T>


/**
 *  a record, with an unknown number of slots. may be full, may be a patch.
 *  used for generic routines that span full records and record patches.
 *  or wherever there's not need to preserve slot types.
 */
 export type GenericRecord = AbstractRecord & Partial<RecordSlots>
 
/**
 *  a public record  is a generic record with the only guarantee that it containes details. 
 *  it may container any other slots too, up to being "complete" with all possible slots.
 */
export type RavRecord = GenericRecord & Pick<RecordSlots, typeof detailsType | typeof authorizationType | typeof photographType> & {

    origin: SubmissionRef
}

/*
    useRavRecordMissingInfo -> useRecordMissingInfo

    fork for externals

    validateMissingInfoForCoreFields --> use warnings 

    test:
        remove a vessel name for an external from the db
        submit 2 auths only
        1 blocking error (on auth)
        1 warning error (on ext miss info)
*/

export const useRavRecordMissingInfo = () => {

    const isEmpty = (str: string | undefined) : boolean => {
        if (!str || str.trim().length === 0) return true
        return false
    }

    const t = useT()
    const vids = useVID()
    const tagmap = useTagCache()

    const { photosRequired } = useAppSettings()


    return (record: RavRecord) => {


        if (record.scope === 'external' || record[delistingType]) 
            return []

        const details = record[detailsType]

        const issues: RecordIssue[] = []

        const imo = vids.find(imoscheme, details.identifiers)
        const regno = vids.find(regnoscheme, details.identifiers)
        const ircs = vids.find(ircsscheme, details.identifiers)

        if (isEmpty(details.vesselKind))
            issues.push({ message: t('valid.missing_kind'), type: 'error', location: vesselKindCategory })

        if (isEmpty(details.vesselType))
            issues.push({ message: t('valid.missing_type'), type: 'error', location: vesselTypeCategory })

        const isFishingVessel = (!isEmpty(details.vesselKind) && details.vesselKind === fishingKindTag) ?? true
        const isCarrierVessel = (!isEmpty(details.vesselKind) && details.vesselKind === carrierKindTag) ?? false

        if (isEmpty(details.name)) 
            issues.push({ message: t('valid.missing_name'), type: 'error', location: "name" })
        
        if (isEmpty(imo)) 
            issues.push({ message: t('valid.missing_imo'), type: 'error', location: imoscheme })
        
        if (isEmpty(regno))
            issues.push({ message: t('valid.missing_regno'), type: 'error', location: regnoscheme })

        if (isEmpty(ircs))
            issues.push({ message: t('valid.missing_ircs'), type: 'error', location: ircsscheme })

        if (isEmpty(`${details.specifications[loaTag]}`))
            issues.push({ message: t('valid.missing_loa'), type: 'error', location: loaTag })

        if (isEmpty(`${details.specifications[tonnageTag]}`))
            issues.push({ message: t('valid.missing_tonnage'), type: 'error', location: tonnageTag })

        if (isEmpty(`${details.specifications[totalVolumeTag]}`))
            issues.push({ message: t('valid.missing_totvol'), type: 'error', location: totalVolumeTag })

        if (isFishingVessel) {
            if (isEmpty(details.range))
                issues.push({ message: t('valid.missing_range'), type: 'error', location: vesselRangeCategory })
            if (!details.gears.length)
                issues.push({ message: t('valid.missing_gear'), type: 'error' })
            if (!details.port)
                issues.push({ message: t('valid.missing_port'), type: 'error', location: portRegistryCategory })
        }

        if (isCarrierVessel) {
            if (isNaN(parseFloat(details.specifications[totalVolumeTag]))) 
                issues.push({ message: t('valid.invalid_totvol'), type: 'error', location: totalVolumeTag })
        }

        const mandatoryContact = (contact: Contact) => isFishingVessel ? true : [ownerTag, operatorTag].includes(contact.type)

        details.contacts.filter(mandatoryContact).forEach(contact => {
        
            if (!contact.name)
                issues.push({ message: `${tagmap.id2name(contact.type).of(contactTypeCategory)} ${t('details.contact_name')}`, type: 'error', location: `${contact.type}-name` })
        
            if (!contact.address)
                issues.push({ message: `${tagmap.id2name(contact.type).of(contactTypeCategory)} ${t('details.contact_address')}`, type: 'error', location: `${contact.type}-address` })
        
            if (isOperatingCompany(contact) && !contact.regno)
                issues.push({ message: `${tagmap.id2name(contact.type).of(contactTypeCategory)} ${t('details.contact_regno')}`, type: 'error', location: `${contact.type}-regno` })
        
        })

        if (photosRequired) {

            const photos = record[photographType]?.photos ?? []

            const missingPhotos = [starboardTag, bowTag, portsideTag]
                                        .map(type => photos.some(p => p.type === type) ? undefined : tagmap.id2name(type).of(photographsTypeCategory))
                                        .filter(entry => entry)

            if (missingPhotos.length > 0)
                issues.push({message: `${t('valid.missing_photos')} (${missingPhotos.join(', ')})`, type: 'error', location: photographType})

        }

        const normalize = (str: string): string =>  {

            const removeLastDot = (str: string): string => str.endsWith('.') ? str.slice(0, -1) : str;

            return removeLastDot(str.replace(t('valid.is_missing'), '')).trim()

        }

        return issues.map(issue => ({...issue, message: normalize(issue.message)}))

    }

}




export type RecordIdentifier = string


export type RecordState = 'uploaded' | 'ignored' | 'submitted' | 'pending' | 'rejected' | 'published'



export type RecordSlots = {

    [detailsType]: Details
    [authorizationType]: Authorization
    [photographType]: Photographs
    [delistingType]: Delisting
    [trxauthzType]: TrxAuthzs
    [priorTrxauthzType]: PriorTrxAuthzs
    [avlType]: AvlDeclarations

}


export const coreSlotTypes = [detailsType, authorizationType, photographType] as const
export const slotTypes = [...coreSlotTypes, delistingType, trxauthzType, priorTrxauthzType, avlType] as const

export type SlotType = typeof slotTypes[number]


export type RecordSlot = {

    /** 
     * internal, opaque from a domain perspective. 
     */
    id: string,

    /** 
     *  dates the information at its source, not necessarily its ingestion.
     *  basis for ordering into history, latest is in current record. 
     */
    timestamp: string
    
}


export const scopeOf = (r: GenericRecord) => r.scope ?? 'rav'
export const cmmOf = (r: GenericRecord) => r.cmm ?? ( scopeOf(r) === 'rav' ? '19/04' : undefined)
export const isRavRecord  = (r: GenericRecord) => scopeOf(r) === 'rav'
export const isDelisted = (r: GenericRecord | undefined) => !!r?.[delistingType]
export const isDelisting = (r: GenericRecord | undefined) => r?.patchedSlots?.includes(delistingType)